import React from "react";

export interface DashboardChartKeywordProps {
  keywords: string[];
  className?: string;
}

// 피그마 디자인 기반 버블 위치 및 크기 (TOP2~5는 absolute로, TOP1은 마지막에 렌더링)
const BUBBLE_CONFIG = [
  {
    // TOP 1 - 중앙 가장 큰 원 (가장 앞에 렌더링)
    size: 120,
    outerSize: 140,
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    gradient: true,
    fontSize: 20,
    fontWeight: "bold",
    showRank: true,
    zIndex: 10,
  },
  {
    // TOP 2 - 좌하단
    size: 100,
    left: "calc(50% - 86px)",
    top: "calc(50% + 48px)",
    transform: "translate(-50%, -50%)",
    bgColor: "#C5ABFA",
    fontSize: 14,
    fontWeight: "bold",
    zIndex: 1,
  },
  {
    // TOP 3 - 우상단
    size: 80,
    left: "calc(50% + 84px)",
    top: "calc(50% - 38px)",
    transform: "translate(-50%, -50%)",
    bgColor: "#B3CAFB",
    fontSize: 14,
    fontWeight: "bold",
    zIndex: 2,
  },
  {
    // TOP 4 - 우하단
    size: 60,
    left: "calc(50% + 106px)",
    top: "calc(50% + 60px)",
    transform: "translate(-50%, -50%)",
    bgColor: "#FFB3CE",
    fontSize: 14,
    fontWeight: "medium",
    zIndex: 3,
  },
  {
    // TOP 5 - 좌상단
    size: 50,
    left: "calc(50% - 91px)",
    top: "calc(50% - 73px)",
    transform: "translate(-50%, -50%)",
    bgColor: "#CCCCCC",
    fontSize: 14,
    fontWeight: "medium",
    zIndex: 4,
  },
];

const DashboardChartKeyword: React.FC<DashboardChartKeywordProps> = ({
  keywords,
  className = "",
}) => {
  // 기본 키워드
  const displayKeywords =
    keywords.length > 0
      ? keywords.slice(0, 5)
      : ["우주", "왜", "수학동아", "촛불", "화성"];

  // TOP1 설정
  const top1Config = BUBBLE_CONFIG[0];
  const top1Keyword = displayKeywords[0];

  // TOP2~5 설정 (먼저 렌더링되어 뒤에 배치)
  const otherConfigs = BUBBLE_CONFIG.slice(1);
  const otherKeywords = displayKeywords.slice(1);

  return (
    <div
      className={`relative w-full min-w-[328px] h-[220px] overflow-hidden p-[10px] flex items-center justify-center ${className}`}
      data-name="dashboard/chart/keyword"
    >
      {/* TOP2~5 원들 (뒤에 배치) */}
      {otherKeywords.map((keyword, index) => {
        const config = otherConfigs[index];
        if (!config) return null;

        return (
          <div
            key={index + 1}
            className="absolute flex items-center justify-center rounded-full text-white text-center"
            style={{
              width: config.size,
              height: config.size,
              left: config.left,
              top: config.top,
              transform: config.transform,
              background: config.bgColor,
              fontSize: config.fontSize,
              fontWeight: config.fontWeight === "bold" ? 700 : 500,
              zIndex: config.zIndex,
            }}
          >
            <span className="tracking-[-0.28px] leading-[16px]">{keyword}</span>
          </div>
        );
      })}

      {/* TOP1 원 (가장 앞에 배치) */}
      {top1Config && (
        <div
          className="relative inline-grid"
          style={{
            zIndex: top1Config.zIndex,
          }}
        >
          {/* 외곽 테두리 원 (그라데이션) */}
          <div
            className="rounded-full"
            style={{
              gridArea: "1 / 1",
              width: top1Config.outerSize,
              height: top1Config.outerSize,
              background:
                "conic-gradient(from 180deg at 50% 50%, #2D8EFA 0deg, #CD3EC7 180deg, #2D8EFA 360deg)",
              opacity: 0.3,
            }}
          />
          {/* 내부 원 (그라데이션 + 콘텐츠) */}
          <div
            className="flex flex-col items-center justify-center rounded-full text-white text-center"
            style={{
              gridArea: "1 / 1",
              width: top1Config.size,
              height: top1Config.size,
              margin: "10px",
              background:
                "linear-gradient(135deg, #cd3ec7 3.87%, #6757fe 49.84%, #2d8efa 95.8%)",
              zIndex: 1,
            }}
          >
            <span className="text-[14px] font-bold leading-[20px] tracking-[-0.28px]">
              TOP 1
            </span>
            <span className="text-[20px] font-bold leading-[28px] tracking-[-0.4px] mt-[20px]">
              {top1Keyword}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardChartKeyword;
